// assets/AllCourses/Exercises/PronunciationExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import useSpeech from "@/hooks/useSpeech";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";

export default function PronunciationExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise?.pronunciationQuestions)
    ? exercise.pronunciationQuestions.map((q) => ({
        textToPronounce:
          typeof q?.textToPronounce === "string"
            ? q.textToPronounce.trim()
            : "",
        ipaSound:
          typeof q?.ipaSound === "string" ? q.ipaSound.trim() : "",
        requireCapitalization: Boolean(q?.requireCapitalization),
      }))
    : [];

  /* ================= CLIENT MOUNT CHECK ================= */
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  /* ================= SPEECH HOOKS ================= */
  const { speak = () => {}, stop = () => {} } = useSpeech();
  const speechRecognition = useSpeechRecognition() || {};

  const {
    transcript = "",
    listening = false,
    startListening = () => {},
    resetTranscript = () => {},
    browserSupportsSpeechRecognition = false,
  } = speechRecognition;

  /* ================= SAFE SUPPORT CHECK ================= */
  const speechSupported =
    mounted &&
    typeof window !== "undefined" &&
    (browserSupportsSpeechRecognition ||
      "SpeechRecognition" in window ||
      "webkitSpeechRecognition" in window);

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    activeIndex: 0,
    answers: {},
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise?.attempt) return;
    setState({
      activeIndex: exercise.attempt.activeIndex ?? 0,
      answers: exercise.attempt.answers ?? {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise?.attempt]);

  /* ================= RESET ON CHANGE ================= */
  useEffect(() => {
    if (!isPreview) return;
    completedRef.current = false;
    resetTranscript();
    stop();
  }, [exercise?._id, isPreview]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[^\w\s]/g, "").replace(/\s+/g, " ");
  };

  const currentQuestion = questions[state.activeIndex];

  /* ================= AUTO EVALUATE SPEECH ================= */
  useEffect(() => {
    if (!isPreview || !transcript || !currentQuestion) return;

    const isCorrect =
      normalize(transcript, currentQuestion.requireCapitalization) ===
      normalize(
        currentQuestion.textToPronounce,
        currentQuestion.requireCapitalization
      );

    setState((prev) => {
      const answers = {
        ...prev.answers,
        [state.activeIndex]: {
          spoken: transcript,
          isCorrect,
        },
      };

      const allAnswered = Object.keys(answers).length === questions.length;
      const allCorrect =
        allAnswered && Object.values(answers).every((a) => a.isCorrect);

      const next = {
        ...prev,
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;
        onComplete(exercise._id, allCorrect);
      }

      return next;
    });

    resetTranscript();
  }, [transcript]);

  /* ================= ADMIN MODE ================= */
  if (isEditMode) {
    const updateQuestions = (next) =>
      onUpdate("pronunciationQuestions", next);

    const handleChange = (idx, field, value) => {
      const next = [...questions];
      next[idx] = { ...next[idx], [field]: String(value || "").trim() };
      updateQuestions(next);
    };

    return (
      <div className="space-y-6">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6 space-y-4">
            <StringField
              label={`Text to Pronounce ${idx + 1}`}
              value={q.textToPronounce}
              onChange={(v) =>
                handleChange(idx, "textToPronounce", v)
              }
              required
            />

            {q.textToPronounce && (
              <SpeakerButton
                text={q.textToPronounce}
                lang="en-GB"
                onPlay={() => speak(q.textToPronounce, "en-GB")}
                onStop={stop}
              />
            )}

            <StringField
              label="IPA"
              value={q.ipaSound}
              onChange={(v) => handleChange(idx, "ipaSound", v)}
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                handleChange(idx, "requireCapitalization", c)
              }
            />
          </Card>
        ))}
      </div>
    );
  }

  /* ================= STUDENT VIEW ================= */
  if (!currentQuestion) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="py-10 text-center">
          No pronunciation items available.
        </CardContent>
      </Card>
    );
  }

  const result = state.answers[state.activeIndex];

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardDescription>
          Pronounce clearly • Item {state.activeIndex + 1} of{" "}
          {questions.length}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8 text-center">
        <div className="text-4xl font-bold text-blue-700">
          {currentQuestion.textToPronounce}
        </div>

        <SpeakerButton
          text={currentQuestion.textToPronounce}
          lang="en-GB"
          size="lg"
          onPlay={() =>
            speak(currentQuestion.textToPronounce, "en-GB")
          }
          onStop={stop}
        />

        {/* 🎤 SPEECH OR FALLBACK */}
        {speechSupported ? (
          <Button
            size="lg"
            onClick={startListening}
            disabled={listening}
            className="w-full max-w-md"
          >
            {listening ? "Listening… 🎤" : "🎤 Click to Speak"}
          </Button>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-orange-600">
              Speech not supported — type your pronunciation instead
            </p>
            <Input
              placeholder="Type what you would say"
              onBlur={(e) =>
                setState((prev) => ({
                  ...prev,
                  answers: {
                    ...prev.answers,
                    [state.activeIndex]: {
                      spoken: e.target.value,
                      isCorrect:
                        normalize(
                          e.target.value,
                          currentQuestion.requireCapitalization
                        ) ===
                        normalize(
                          currentQuestion.textToPronounce,
                          currentQuestion.requireCapitalization
                        ),
                    },
                  },
                }))
              }
            />
          </div>
        )}

        {result && (
          <div
            className={
              result.isCorrect ? "text-green-600" : "text-red-600"
            }
          >
            <p className="text-2xl font-bold">
              {result.isCorrect ? "Correct! ✅" : "Try Again 🔄"}
            </p>
            <p className="mt-3 text-lg">
              You said: <strong>{result.spoken}</strong>
            </p>
          </div>
        )}

        {result && state.activeIndex < questions.length - 1 && (
          <Button
            size="lg"
            onClick={() =>
              setState((p) => ({
                ...p,
                activeIndex: p.activeIndex + 1,
              }))
            }
          >
            Next →
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
